import { z } from 'zod'

import type { StockParams } from '@/hooks/queries/stock'
import { codeList } from '@/lib/codes'

/**
 * The view state the Get Stock page reads from the URL.
 *
 * The warehouse is the one required choice. The two switches change what is
 * summed (allotted boxes into box quantity; blocked out of available), so they
 * are view state and travel in the link.
 */
export const stockSearchSchema = z.object({
  warehouse: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The warehouse whose stock is shown; nothing loads without it.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the list to show, from 1.'),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Rows per page, 1 to 100.'),
  skus: z
    .string()
    .optional()
    .catch(undefined)
    .describe('SKU codes to show, comma-separated, exact.'),
  name: z.string().optional().catch(undefined).describe('Text matched against the SKU name.'),
  brand: z.string().optional().catch(undefined).describe('The brand name to narrow to.'),
  vendor: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The vendor id to narrow to.'),
  retailer: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The retailer id to narrow to.'),
  retailerSkus: z
    .string()
    .optional()
    .catch(undefined)
    .describe('Retailer SKUs to show, comma-separated, exact.'),
  allotted: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe('Count boxes already allotted to a purchase order in the box quantity.'),
  netBlocked: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe('Take blocked units out of the available quantity.'),
})

export type StockSearch = z.infer<typeof stockSearchSchema>

export const STOCK_SEARCH_DEFAULTS = { page: 1, limit: 20 } as const

/** Drop any value that equals its default, or is empty, so a URL carries only what differs. */
export function withoutSearchDefaults(search: StockSearch): StockSearch {
  const pruned: StockSearch = {}
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.page !== undefined && search.page !== STOCK_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.limit !== undefined && search.limit !== STOCK_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.skus) pruned.skus = search.skus
  if (search.name) pruned.name = search.name
  if (search.brand) pruned.brand = search.brand
  if (search.vendor !== undefined) pruned.vendor = search.vendor
  if (search.retailer !== undefined) pruned.retailer = search.retailer
  if (search.retailerSkus) pruned.retailerSkus = search.retailerSkus
  if (search.allotted) pruned.allotted = true
  if (search.netBlocked) pruned.netBlocked = true
  return pruned
}

/** What the endpoint is asked for, or `undefined` while no warehouse is chosen. */
export function toStockParams(search: StockSearch): StockParams | undefined {
  if (search.warehouse === undefined) return undefined
  return {
    warehouseId: search.warehouse,
    page: search.page ?? STOCK_SEARCH_DEFAULTS.page,
    limit: search.limit ?? STOCK_SEARCH_DEFAULTS.limit,
    skuCodes: codeList(search.skus),
    name: search.name || undefined,
    brand: search.brand || undefined,
    vendorId: search.vendor,
    retailerId: search.retailer,
    retailerSkus: codeList(search.retailerSkus),
    includeAllotted: search.allotted === true,
    excludeBlocked: search.netBlocked === true,
  }
}
